import React, { useState } from 'react';
import { ShoppingCart, User, Heart, Search, Menu, Plus, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template15 = () => {
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = useState('all');

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Ultra Clean Header */}
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template15')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            MODERN EDGE
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template15/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template15/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template15')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template15/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template15/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template15/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Emotional Hero - Minimalist */}
            <section className="px-6 lg:px-12 py-16 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
                        <div className="lg:col-span-5 space-y-8">
                            <div className="space-y-6">
                                <h2 className="text-4xl lg:text-6xl xl:text-7xl font-extralight text-gray-900 leading-[0.9] tracking-tight">
                                    Less is<br/>
                                    <span className="font-light">everything</span>
                                </h2>
                                <div className="w-16 h-px bg-gray-900"></div>
                                <p className="text-lg lg:text-xl text-gray-600 font-light leading-relaxed max-w-md">
                                    Discover timeless pieces crafted with intention.
                                    Every item tells a story of quality, simplicity, and purpose.
                                </p>
                            </div>
                            <button className="group relative overflow-hidden">
                                <span className="relative z-10 inline-flex items-center gap-3 px-8 py-4 bg-gray-900 text-white text-sm font-light tracking-wider transition-all duration-300 hover:tracking-widest">
                                    EXPLORE COLLECTION
                                    <Plus size={16} strokeWidth={1.5} className="group-hover:rotate-90 transition-transform duration-300" />
                                </span>
                            </button>
                        </div>

                        <div className="lg:col-span-7">
                            <div className="relative aspect-[4/3] lg:aspect-[3/2] overflow-hidden">
                                <img
                                    src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=800&fit=crop"
                                    alt="Minimalist Collection"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gray-900/5"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Category Navigation - Ultra Clean */}
            <section className="px-6 lg:px-12 py-12 lg:py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 lg:mb-16">
                        <h3 className="text-2xl lg:text-3xl font-extralight text-gray-900 mb-4">Collections</h3>
                        <div className="w-8 h-px bg-gray-900 mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                        {[
                            { title: "Women", count: "250", image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600&h=800&fit=crop" },
                            { title: "Men", count: "180", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop" },
                            { title: "Essentials", count: "120", image: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600&h=800&fit=crop" }
                        ].map((category, index) => (
                            <div key={index} className="group cursor-pointer">
                                <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 mb-6">
                                    <img
                                        src={category.image}
                                        alt={category.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-500"></div>
                                </div>
                                <div className="text-center space-y-2">
                                    <h4 className="text-xl font-light text-gray-900 tracking-wide">{category.title}</h4>
                                    <p className="text-sm text-gray-500 font-light">{category.count} pieces</p>
                                    <div className="w-6 h-px bg-gray-300 mx-auto group-hover:bg-gray-900 transition-colors duration-300"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Product Grid - Sophisticated */}
            <section className="px-6 lg:px-12 py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="flex justify-between items-end mb-12 lg:mb-16">
                        <div>
                            <h3 className="text-2xl lg:text-3xl font-extralight text-gray-900 mb-2">Featured</h3>
                            <div className="w-8 h-px bg-gray-900"></div>
                        </div>
                        <div className="flex items-center gap-8 text-sm font-light text-gray-500">
                            {['All', 'New', 'Essential', 'Archive'].map((filter) => (
                                <button
                                    key={filter}
                                    onClick={() => setSelectedCategory(filter.toLowerCase())}
                                    className={`tracking-wider transition-colors duration-300 ${
                                        selectedCategory === filter.toLowerCase() || (selectedCategory === 'all' && filter === 'All')
                                            ? 'text-gray-900 border-b border-gray-900 pb-1'
                                            : 'hover:text-gray-900'
                                    }`}
                                >
                                    {filter}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-12">
                        {Array.from({ length: 16 }, (_, index) => (
                            <div key={index} className="group">
                                <div className="relative aspect-[3/4] overflow-hidden bg-gray-50 mb-4">
                                    <img
                                        src={`https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=500&h=700&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=${index * 41 + 2000}`}
                                        alt={`Item ${index + 1}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <button className="absolute top-4 right-4 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <Heart size={16} strokeWidth={1.5} className="text-gray-700" />
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-sm font-light text-gray-900 tracking-wide">Essential Piece {index + 1}</h4>
                                    <p className="text-lg font-light text-gray-900">${(85 + index * 15)}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-16 lg:mt-20">
                        <button className="group relative overflow-hidden">
                            <span className="relative z-10 inline-flex items-center gap-3 px-8 py-3 border border-gray-300 text-gray-700 text-sm font-light tracking-wider transition-all duration-300 hover:border-gray-900 hover:text-gray-900">
                                VIEW ALL
                                <Plus size={16} strokeWidth={1.5} className="group-hover:rotate-90 transition-transform duration-300" />
                            </span>
                        </button>
                    </div>
                </div>
            </section>

            {/* Brand Story - Emotional */}
            <section className="px-6 lg:px-12 py-16 lg:py-24 bg-gray-50">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="space-y-8 lg:space-y-12">
                        <div className="w-12 h-px bg-gray-900 mx-auto"></div>
                        <h3 className="text-3xl lg:text-5xl font-extralight text-gray-900 leading-tight">
                            Crafted with<br/>intention
                        </h3>
                        <p className="text-lg lg:text-xl text-gray-600 font-light leading-relaxed max-w-2xl mx-auto">
                            Every piece in our collection represents a commitment to quality, sustainability, and timeless design.
                            We believe in creating less, but better.
                        </p>
                        <div className="flex justify-center gap-16 lg:gap-24 pt-8">
                            {[
                                { label: "Sustainable", value: "100%" },
                                { label: "Quality", value: "Premium" },
                                { label: "Timeless", value: "Always" }
                            ].map((stat, index) => (
                                <div key={index} className="text-center space-y-2">
                                    <div className="text-2xl font-extralight text-gray-900">{stat.value}</div>
                                    <div className="text-sm font-light text-gray-500 tracking-wide">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Video Section - Minimal */}
            <section className="px-6 lg:px-12 py-16 lg:py-24 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12 lg:mb-16">
                        <h3 className="text-2xl lg:text-3xl font-extralight text-gray-900 mb-4">Our Story</h3>
                        <div className="w-8 h-px bg-gray-900 mx-auto"></div>
                    </div>

                    <div className="relative aspect-video bg-gray-100 overflow-hidden">
                        <img
                            src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=675&fit=crop"
                            alt="Brand Story Video"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-white/10"></div>
                        <button className="absolute inset-0 flex items-center justify-center group">
                            <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                                <div className="w-6 h-6 border-t-8 border-r-8 border-t-transparent border-r-gray-900 ml-1"></div>
                            </div>
                        </button>
                    </div>
                </div>
            </section>

            {/* Newsletter - Clean */}
            <section className="px-6 lg:px-12 py-16 lg:py-20 bg-gray-900">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="space-y-8">
                        <div className="space-y-4">
                            <h3 className="text-2xl lg:text-3xl font-extralight text-white">Stay connected</h3>
                            <p className="text-gray-400 font-light">Receive updates on new collections and exclusive access to our seasonal pieces.</p>
                        </div>
                        <div className="flex gap-4 max-w-md mx-auto">
                            <input
                                type="email"
                                placeholder="Your email"
                                className="flex-1 px-6 py-4 bg-transparent border border-gray-700 text-white placeholder-gray-500 focus:border-white focus:outline-none transition-colors duration-300 font-light"
                            />
                            <button className="px-8 py-4 bg-white text-gray-900 font-light tracking-wider hover:bg-gray-100 transition-colors duration-300">
                                JOIN
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Minimal Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">MODERN EDGE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template15')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template15/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template15/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template15/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@modernedge.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 MODERN EDGE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template15;